import { inject } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { firstValueFrom, type Observable, take } from 'rxjs';
import { AlertDialogService } from '../../alert-dialog/alert-dialog.service';
import { BottomToastService } from '../../bottom-toast/bottom-toast.service';

export type ConfirmVariant = 'default' | 'destructive' | 'success';

export interface ConfirmDialogLike {
  open(options: {
    title: string;
    description: string;
    cancelLabel?: string;
    actionLabel?: string;
    actionVariant?: ConfirmVariant;
  }): Observable<boolean>;
}

export type BottomToastVariant = 1 | 2 | 3 | 4 | 5;

export interface BottomToastLike {
  open(options: {
    message: string;
    variant?: BottomToastVariant;
    durationSeconds?: number;
    showClose?: boolean;
  }): string | number;
}

export type MutationSuccessPredicate<TResult> = (result: TResult) => boolean;

export type MutateFn<TInput, TResult> = (input: TInput) => Promise<TResult>;

/** Default guard for API payloads shaped as `{ success: boolean; data: T }`. */
export function isMutationResultSuccess<TResult>(result: TResult): boolean {
  if (result !== null && typeof result === 'object' && 'success' in result) {
    return Boolean((result as { success?: boolean }).success);
  }
  return true;
}

type MutationToastMessages = {
  successMessageKey?: string;
  successMessageParams?: Record<string, unknown>;
  successToastVariant?: BottomToastVariant;
  errorMessageKey?: string;
  errorMessageParams?: Record<string, unknown>;
  errorToastVariant?: BottomToastVariant;
};

export type MutateWithToastParams<TInput, TResult> = MutationToastMessages & {
  toast: BottomToastLike;
  transloco: TranslocoService;
  mutate: MutateFn<TInput, TResult>;
  input: TInput;
  isSuccess?: MutationSuccessPredicate<TResult>;
  onSuccess?: (result: TResult) => void | Promise<void>;
  onError?: (err: unknown) => void;
};

export type MutateWithToastOptions<TInput, TResult> = Omit<
  MutateWithToastParams<TInput, TResult>,
  'toast' | 'transloco'
>;

export type ConfirmAndMutateParams<TInput, TResult> = MutateWithToastParams<TInput, TResult> & {
  alert: ConfirmDialogLike;
  titleKey: string;
  titleParams?: Record<string, unknown>;
  descriptionKey: string;
  descriptionParams?: Record<string, unknown>;
  cancelLabelKey?: string;
  cancelLabelParams?: Record<string, unknown>;
  actionLabelKey: string;
  actionLabelParams?: Record<string, unknown>;
  actionVariant?: ConfirmVariant;
};

export type ConfirmAndMutateOptions<TInput, TResult> = Omit<
  ConfirmAndMutateParams<TInput, TResult>,
  'alert' | 'toast' | 'transloco'
>;

export type ConfirmAndDeleteParams<TInput, TResult = unknown> = {
  alert: ConfirmDialogLike;
  toast: BottomToastLike;
  transloco: TranslocoService;
  titleKey?: string;
  titleParams?: Record<string, unknown>;
  descriptionKey?: string;
  descriptionParams?: Record<string, unknown>;
  mutate: MutateFn<TInput, TResult>;
  input: TInput;
  isSuccess?: MutationSuccessPredicate<TResult>;
  successMessageKey?: string;
  successMessageParams?: Record<string, unknown>;
  errorMessageKey?: string;
  errorMessageParams?: Record<string, unknown>;
  onSuccess?: (result: TResult) => void | Promise<void>;
  onError?: (err: unknown) => void;
};

export type ConfirmAndDeleteOptions<TInput, TResult = unknown> = Omit<
  ConfirmAndDeleteParams<TInput, TResult>,
  'alert' | 'toast' | 'transloco'
>;

type RunMutationWithToastParams<TInput, TResult> = MutateWithToastParams<TInput, TResult>;

async function runMutationWithToast<TInput, TResult>(
  params: RunMutationWithToastParams<TInput, TResult>,
): Promise<TResult | undefined> {
  const isSuccess = params.isSuccess ?? isMutationResultSuccess;

  try {
    const result = await params.mutate(params.input);

    if (!isSuccess(result)) {
      params.toast.open({
        message: params.transloco.translate(
          params.errorMessageKey ?? 'common.messages.error',
          params.errorMessageParams,
        ),
        variant: params.errorToastVariant ?? 5,
      });
      params.onError?.(result);
      return undefined;
    }

    params.toast.open({
      message: params.transloco.translate(
        params.successMessageKey ?? 'common.messages.success',
        params.successMessageParams,
      ),
      variant: params.successToastVariant ?? 3,
    });

    await Promise.resolve(params.onSuccess?.(result));
    return result;
  } catch (err: unknown) {
    params.toast.open({
      message: params.transloco.translate(
        params.errorMessageKey ?? 'common.messages.error',
        params.errorMessageParams,
      ),
      variant: params.errorToastVariant ?? 5,
    });

    params.onError?.(err);
    return undefined;
  }
}

export async function mutateWithToast<TInput, TResult = unknown>(
  params: RunMutationWithToastParams<TInput, TResult>,
): Promise<TResult | undefined> {
  return runMutationWithToast(params);
}

export async function addWithToast<TInput, TResult = unknown>(
  params: Omit<RunMutationWithToastParams<TInput, TResult>, 'successMessageKey'> & {
    successMessageKey?: string;
  },
): Promise<TResult | undefined> {
  return runMutationWithToast({
    ...params,
    successMessageKey: params.successMessageKey ?? 'common.messages.success',
  });
}

export async function updateWithToast<TInput, TResult = unknown>(
  params: Omit<RunMutationWithToastParams<TInput, TResult>, 'successMessageKey'> & {
    successMessageKey?: string;
  },
): Promise<TResult | undefined> {
  return runMutationWithToast({
    ...params,
    successMessageKey: params.successMessageKey ?? 'common.messages.success',
  });
}

export async function confirmAndMutate<TInput, TResult = unknown>(
  params: ConfirmAndMutateParams<TInput, TResult>,
): Promise<boolean> {
  const title = params.transloco.translate(params.titleKey, params.titleParams);
  const description = params.transloco.translate(params.descriptionKey, params.descriptionParams);
  const cancelLabel = params.transloco.translate(
    params.cancelLabelKey ?? 'common.actions.cancel',
    params.cancelLabelParams,
  );
  const actionLabel = params.transloco.translate(params.actionLabelKey, params.actionLabelParams);

  const confirmed = await firstValueFrom(
    params.alert
      .open({
        title,
        description,
        cancelLabel,
        actionLabel,
        actionVariant: params.actionVariant ?? 'default',
      })
      .pipe(take(1)),
  );

  if (!confirmed) {
    return false;
  }

  const result = await runMutationWithToast(params);
  return result !== undefined;
}

export async function confirmAndDelete<TInput, TResult = unknown>(
  params: ConfirmAndDeleteParams<TInput, TResult>,
): Promise<boolean> {
  return confirmAndMutate({
    ...params,
    titleKey: params.titleKey ?? 'common.messages.confirm',
    descriptionKey: params.descriptionKey ?? 'common.messages.deleteConfirm',
    actionLabelKey: 'common.actions.delete',
    actionVariant: 'destructive',
  });
}

type AddOrUpdateWithToastOptions<TInput, TResult> = Omit<
  MutateWithToastOptions<TInput, TResult>,
  'successMessageKey'
> & {
  successMessageKey?: string;
};

export interface MutationActions {
  mutateWithToast<TInput, TResult = unknown>(
    params: MutateWithToastOptions<TInput, TResult>,
  ): Promise<TResult | undefined>;
  addWithToast<TInput, TResult = unknown>(
    params: AddOrUpdateWithToastOptions<TInput, TResult>,
  ): Promise<TResult | undefined>;
  updateWithToast<TInput, TResult = unknown>(
    params: AddOrUpdateWithToastOptions<TInput, TResult>,
  ): Promise<TResult | undefined>;
  confirmAndMutate<TInput, TResult = unknown>(
    params: ConfirmAndMutateOptions<TInput, TResult>,
  ): Promise<boolean>;
  confirmAndDelete<TInput, TResult = unknown>(
    params: ConfirmAndDeleteOptions<TInput, TResult>,
  ): Promise<boolean>;
}

/**
 * Injects AlertDialogService, BottomToastService and TranslocoService once and
 * returns the mutation-action helpers above bound to them, so call sites only
 * pass the parts that differ per action (mutate, input, messages, callbacks).
 */
export function injectMutationActions(): MutationActions {
  const alert = inject(AlertDialogService);
  const toast = inject(BottomToastService);
  const transloco = inject(TranslocoService);

  return {
    mutateWithToast(params) {
      return mutateWithToast({ ...params, toast, transloco });
    },
    addWithToast(params) {
      return addWithToast({ ...params, toast, transloco });
    },
    updateWithToast(params) {
      return updateWithToast({ ...params, toast, transloco });
    },
    confirmAndMutate(params) {
      return confirmAndMutate({ ...params, alert, toast, transloco });
    },
    confirmAndDelete(params) {
      return confirmAndDelete({ ...params, alert, toast, transloco });
    },
  };
}
